/**
 * Flatten TipTap JSON to plain text.
 *
 * Used for structured data and meta descriptions, where a search engine wants
 * the words and nothing else. Rendering to text rather than stripping HTML
 * means there is never markup to escape in the first place.
 */
export function plainText(doc: { content?: unknown[] } | null | undefined): string {
  if (!doc?.content) return "";

  const walk = (node: unknown): string => {
    if (typeof node !== "object" || node === null) return "";
    const record = node as { text?: string; content?: unknown[] };
    if (record.text) return record.text;
    return (record.content ?? []).map(walk).join(" ");
  };

  return doc.content.map(walk).join(" ").replace(/\s+/g, " ").trim();
}

/**
 * Split a document into heading-led groups.
 *
 * A heading and the copy beneath it are one thought, so they arrive together;
 * the next heading starts the next group. Grouping is what keeps a revealed
 * passage from turning into a queue — fifteen paragraphs animating one after
 * another would take longer to finish than a reader takes to scroll past them,
 * whereas four groups each arrive as the reader reaches them.
 *
 * Anything before the first heading forms a group of its own, so a document
 * that opens with a paragraph is not swallowed by the heading that follows it.
 */
export function groupByHeading<T extends { type?: string }>(nodes: readonly T[]): T[][] {
  const groups: T[][] = [];

  for (const node of nodes) {
    const current = groups[groups.length - 1];
    if (node.type === "heading" || !current) groups.push([node]);
    else current.push(node);
  }

  return groups;
}

/** Trim to a whole word, for a meta description that must fit a length budget. */
export function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : max).trimEnd()}…`;
}
